/**
 * LowCreditsAlert — sticky banner shown when user runs low on Premium Credits.
 * Suggests 2 paths:
 *   1) Earn MedPoints (with deep-links to specific quest actions)
 *   2) Buy a top-up (deep-link to /wallet Premium Credits section)
 *
 * Appears when balance < threshold (default 5 credits). Dismissible for 1h.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ShoppingBag, Gift, X, MessageSquare, BookOpen, ThumbsUp, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'low_credits_alert_dismissed_until';

export default function LowCreditsAlert({ threshold = 5 }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'es').startsWith('en') ? 'en' : 'es';
  const T = (es, en) => (lang === 'en' ? en : es);
  const [credits, setCredits] = useState(null);
  const [medpoints, setMedpoints] = useState(0);
  const [mpPerCredit, setMpPerCredit] = useState(200);
  const [dismissed, setDismissed] = useState(false);

  const email = typeof window !== 'undefined' ? (
    localStorage.getItem('userEmail') ||
    localStorage.getItem('communityUserEmail') ||
    localStorage.getItem('admin_email') || ''
  ) : '';

  useEffect(() => {
    if (!email) return;
    const dismissUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissUntil > Date.now()) {
      setDismissed(true);
      return;
    }
    const fetchWallets = async () => {
      try {
        const [w, t] = await Promise.all([
          fetch(`${API}/api/economy/wallets?user_id=${encodeURIComponent(email)}`).then(r => r.json()),
          fetch(`${API}/api/economy/status-tier/${encodeURIComponent(email)}`).then(r => r.json()),
        ]);
        const ws = w.wallets || {};
        const pc = (ws.premium?.balance || 0) + (ws.bonus?.balance || 0) + (ws.sponsored?.balance || 0);
        setCredits(pc);
        setMedpoints(ws.medpoints?.balance || 0);
        setMpPerCredit(t.mp_per_credit || 200);
      } catch { /* ignore */ }
    };
    fetchWallets();
    const onRefresh = () => fetchWallets();
    window.addEventListener('medetechni:wallet-refresh', onRefresh);
    const interval = setInterval(fetchWallets, 60000);
    return () => {
      window.removeEventListener('medetechni:wallet-refresh', onRefresh);
      clearInterval(interval);
    };
  }, [email]);

  if (dismissed || credits === null || credits >= threshold) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 3600 * 1000));
    setDismissed(true);
  };

  const mpNeededForOne = Math.max(0, mpPerCredit - (medpoints % mpPerCredit));
  const creditsFromMP = Math.floor(medpoints / mpPerCredit);

  return (
    <div className="fixed bottom-24 right-4 sm:right-6 z-[9990] w-[min(360px,calc(100vw-2rem))] bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-2xl shadow-2xl p-4" data-testid="low-credits-alert">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center">
          <AlertCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            {T('Te quedan', 'You have')} <span className="text-amber-700">{credits} {T('créditos', 'credits')}</span>
          </p>
          <p className="text-xs text-slate-700 mt-0.5">
            {T('Tenés 2 caminos para seguir analizando:', 'You have 2 paths to keep analyzing:')}
          </p>
        </div>
        <button onClick={dismiss} className="text-slate-500 hover:text-slate-900 shrink-0" data-testid="low-credits-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* MedPoints progress */}
      {creditsFromMP > 0 ? (
        <div className="mt-3 p-3 bg-white rounded-xl border border-emerald-300" data-testid="low-credits-mp-redeem">
          <p className="text-xs text-emerald-800 font-semibold mb-1">
            {T(`Ya tenés ${medpoints} MP → canjea por ${creditsFromMP} crédito${creditsFromMP > 1 ? 's' : ''} gratis`,
              `You already have ${medpoints} MP → redeem for ${creditsFromMP} free credit${creditsFromMP > 1 ? 's' : ''}`)}
          </p>
          <button
            onClick={() => navigate('/wallet?section=redeem')}
            className="w-full mt-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1"
            data-testid="low-credits-go-redeem"
          >
            <Gift className="w-3 h-3" /> {T('Canjear MedPoints ahora', 'Redeem MedPoints now')}
          </button>
        </div>
      ) : (
        <div className="mt-3 p-3 bg-white rounded-xl border border-emerald-300">
          <p className="text-xs text-slate-800 font-semibold mb-2">
            {T(`Gana ${mpNeededForOne} MP más para tu próximo crédito gratis:`,
              `Earn ${mpNeededForOne} more MP for your next free credit:`)}
          </p>
          <div className="space-y-1.5">
            <QuestLink icon={Activity} label={T('Usá 1 solución (+15 MP)', 'Use 1 solution (+15 MP)')} to="/my-solutions" testid="low-credits-quest-solution" />
            <QuestLink icon={BookOpen} label={T('Leé 1 post (+10 MP)', 'Read 1 post (+10 MP)')} to="/community" testid="low-credits-quest-post" />
            <QuestLink icon={MessageSquare} label={T('Comentá algo (+15 MP)', 'Leave a comment (+15 MP)')} to="/community" testid="low-credits-quest-comment" />
            <QuestLink icon={ThumbsUp} label={T('Dejá feedback (+20 MP)', 'Submit feedback (+20 MP)')} to="/community?tab=suggestions" testid="low-credits-quest-feedback" />
          </div>
        </div>
      )}

      {/* Buy top-up */}
      <button
        onClick={() => navigate('/wallet?section=buy-credits')}
        className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5"
        data-testid="low-credits-buy"
      >
        <ShoppingBag className="w-3.5 h-3.5" /> {T('Comprar top-up (desde $4.99)', 'Buy top-up (from $4.99)')}
      </button>
      <p className="mt-2 text-[10px] text-slate-600 text-center">
        {T('O cambiá a un plan superior en /choose-plan', 'Or upgrade to a higher plan at /choose-plan')}
      </p>
    </div>
  );
}

const QuestLink = ({ icon: Icon, label, to, testid }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} className="w-full flex items-center gap-2 text-left text-xs text-slate-900 hover:text-emerald-700 hover:bg-emerald-50 px-2 py-1.5 rounded" data-testid={testid}>
      <Icon className="w-3 h-3 text-emerald-600 shrink-0" />
      <span className="flex-1">{label}</span>
      <span className="text-emerald-600 text-[10px]">→</span>
    </button>
  );
};
